import React, { useCallback, useRef } from 'react';

import Segmented from 'components/DesignSystem/Segmented';
import Toast from 'components/DesignSystem/Toast';
import Breadcrumbs from 'components/FileViewer/Breadcrumbs';
import CompanySelector from 'components/FileViewer/CompanySelector';
import EmptyState from 'components/FileViewer/EmptyState';
import EntryList from 'components/FileViewer/EntryList';
import EntryTable from 'components/FileViewer/EntryTable';
import ExplorerIcon from 'components/FileViewer/ExplorerIcon';
import FolderTree from 'components/FileViewer/FolderTree';
import LoadingState from 'components/FileViewer/LoadingState';
import ManageActions from 'components/FileViewer/ManageActions';
import QuickAccess from 'components/FileViewer/QuickAccess';
import ResultsBar, { resultsLabel } from 'components/FileViewer/ResultsBar';
import SearchBar from 'components/FileViewer/SearchBar';
import SelectionBar from 'components/FileViewer/SelectionBar';
import TransferStatus from 'components/FileViewer/TransferStatus';
import TypeFilters from 'components/FileViewer/TypeFilters';
import useElementWidth from 'utils/hooks/useElementWidth';
import useIsMobile from 'utils/hooks/useIsMobile';
import useFileExplorer from 'utils/hooks/file_viewer/useFileExplorer';
import useFileDrop from 'utils/hooks/file_viewer/useFileDrop';
import useSearchShortcut from 'utils/hooks/file_viewer/useSearchShortcut';
import { useListFileViewerCompaniesQuery } from 'services/api/fileViewer';
import { SearchScope } from 'helper/products/fileExplorer';
import { companyLabel } from 'helper/products/fileViewerCompanies';

import './index.scss';

/** Abaixo disto a árvore lateral espremeria a tabela (sidebar do app aberta em notebook). */
const TREE_MIN_WIDTH = 760;
/** Largura útil da tabela a partir da qual cabem as colunas Tamanho e data com hora. */
const WIDE_TABLE_MIN_WIDTH = 640;
const TREE_GAP = 20;

const SCOPE_OPTIONS = [
  { value: 'here' as SearchScope, label: 'Nesta pasta' },
  { value: 'all' as SearchScope, label: 'Todas as pastas' },
];

export default function FileExplorer() {
  const isMobile = useIsMobile();
  const explorer = useFileExplorer();
  const rootRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const width = useElementWidth(rootRef);
  useSearchShortcut(searchRef);

  const {
    currentFolder, path, canManage, transfers, download, visible,
    canChooseCompany, company, setCompany, needsCompany,
  } = explorer;

  // Nome da empresa escolhida para o vazio da raiz: a mesma consulta do seletor (uma request só,
  // cache compartilhado); sem o seletor, não é pedida.
  const { currentData: companies } = useListFileViewerCompaniesQuery(undefined, {
    skip: !canChooseCompany,
  });
  const companyName = canChooseCompany && company && company !== 'todas'
    ? companyLabel(company, companies)
    : undefined;

  const { uploadFiles } = transfers;
  const upload = useCallback(
    (files: File[]) => uploadFiles(files, currentFolder),
    [uploadFiles, currentFolder],
  );
  const drop = useFileDrop({ enabled: canManage && !transfers.uploadProgress, onFiles: upload });

  // Largura 0 = ainda não medida (1º render, testes): assume o layout largo.
  const measured = width > 0;
  const showTree = !isMobile && (!measured || width >= TREE_MIN_WIDTH);
  const treeWidth = measured && width < 1100 ? 210 : 240;
  const tableWidth = measured ? width - (showTree ? treeWidth + TREE_GAP : 0) : Infinity;
  const wideTable = tableWidth >= WIDE_TABLE_MIN_WIDTH;

  const entryActions = {
    onOpenFolder: explorer.goTo,
    onDownload: download.downloadFile,
    downloadingKeys: download.downloadingKeys,
    isFavorite: explorer.isFavorite,
    onTogglePin: explorer.togglePin,
  };

  const hasTerms = visible.terms.length > 0;
  // Com a lista na tela (nem esperando a escolha da empresa, nem carregando).
  const showList = !needsCompany && !explorer.isLoading;
  const showQuickAccess = explorer.showQuickAccess && showList
    && (explorer.favoriteFolders.length > 0 || !isMobile);

  return (
    <div
      ref={rootRef}
      className={`file-explorer${isMobile ? ' is-mobile' : ''}`}
      style={{ '--file-explorer-tree-width': `${treeWidth}px` } as React.CSSProperties}
    >
      <header className="file-explorer-header">
        <div className="file-explorer-heading">
          <h1 className="file-explorer-title">MEUS ARQUIVOS</h1>
          <Breadcrumbs path={path} onNavigate={explorer.goTo} />
        </div>
        {canManage && (
          <ManageActions
            folder={currentFolder}
            isCreating={transfers.isCreating}
            isUploading={!!transfers.uploadProgress}
            onCreateFolder={(name) => transfers.createFolder(name, currentFolder)}
            onUpload={upload}
          />
        )}
      </header>

      <div className={`file-explorer-toolbar${canChooseCompany ? ' has-company' : ''}`}>
        {canChooseCompany && (
          <CompanySelector value={company} viewerUserId={explorer.userId} onChange={setCompany} />
        )}
        <SearchBar
          inputRef={searchRef}
          value={explorer.query}
          onChange={explorer.setQuery}
          showShortcutHint={!isMobile}
          compact={isMobile}
        />
        <Segmented
          options={SCOPE_OPTIONS}
          value={explorer.scope}
          onChange={explorer.setScope}
          variant={isMobile ? 'default' : 'filled'}
          fill={isMobile}
          ariaLabel="Onde buscar"
          className="file-explorer-scope"
        />
      </div>

      {showList && (
        <TypeFilters kinds={visible.kinds} active={explorer.kind} onSelect={explorer.setKind} />
      )}

      {showQuickAccess && (
        <QuickAccess folders={explorer.favoriteFolders} onOpen={explorer.goTo} />
      )}

      <div className="file-explorer-body">
        {showTree && showList && (
          <FolderTree
            root={explorer.root}
            activeKey={currentFolder.key}
            expanded={explorer.expanded}
            onToggle={explorer.toggleExpanded}
            onOpen={explorer.goTo}
          />
        )}

        <section
          className={`file-explorer-content${drop.isDragging ? ' is-drop-target' : ''}`}
          aria-busy={!needsCompany && explorer.isLoading}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...drop.handlers}
        >
          {needsCompany && (
            <EmptyState
              query=""
              isRoot
              canUpload={false}
              onShowAllCompanies={() => setCompany('todas')}
            />
          )}

          {!needsCompany && explorer.isLoading && <LoadingState showTree={showTree} />}

          {showList && (
            <>
              <ResultsBar
                label={resultsLabel({
                  entries: visible.entries,
                  isGlobal: visible.isGlobal,
                  hasTerms,
                  folderName: currentFolder.name,
                })}
                updatedAt={explorer.updatedAt}
                isRefreshing={explorer.isRefreshing}
                canForceRefresh={explorer.canForceRefresh}
                onRefresh={() => explorer.refresh({ force: explorer.canForceRefresh })}
                sortSelect={isMobile
                  ? { value: explorer.sortKey, onChange: explorer.setSortKey }
                  : undefined}
              />

              <TransferStatus progress={transfers.uploadProgress} />

              {!isMobile && explorer.selectedFiles.length > 0 && (
                <SelectionBar
                  files={explorer.selectedFiles}
                  progress={download.zipProgress}
                  onDownload={() => download.downloadZip(explorer.selectedFiles, path)}
                  onCancel={download.cancelZip}
                  onClear={explorer.clearSelection}
                />
              )}

              {explorer.shownEntries.length > 0 && (isMobile ? (
                <EntryList
                  entries={explorer.shownEntries}
                  showPath={visible.isGlobal}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...entryActions}
                />
              ) : (
                <EntryTable
                  entries={explorer.shownEntries}
                  showPath={visible.isGlobal}
                  wide={wideTable}
                  sortKey={explorer.sortKey}
                  sortDir={explorer.sortDir}
                  onSort={explorer.sortBy}
                  selectedKeys={explorer.selectedKeys}
                  onToggleSelected={explorer.toggleSelected}
                  allSelected={explorer.allVisibleSelected}
                  someSelected={explorer.someVisibleSelected}
                  hasSelectableFiles={explorer.hasSelectableFiles}
                  onToggleAll={explorer.toggleAllVisible}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...entryActions}
                />
              ))}

              {explorer.hiddenCount > 0 && (
                <button type="button" className="file-explorer-more" onClick={explorer.showMore}>
                  {`Mostrar mais (${explorer.shownEntries.length.toLocaleString('pt-BR')} de ${visible.entries.length.toLocaleString('pt-BR')})`}
                </button>
              )}

              {visible.entries.length === 0 && (
                <EmptyState
                  query={explorer.query}
                  isRoot={path.length === 0 && !hasTerms}
                  canUpload={canManage}
                  onSearchEverywhere={explorer.scope === 'here' ? () => explorer.setScope('all') : undefined}
                  companyName={companyName}
                />
              )}
            </>
          )}

          {drop.isDragging && (
            <div className="file-explorer-drop" aria-hidden="true">
              <ExplorerIcon name="upload" size={28} />
              <span>
                Solte para enviar para
                {' '}
                <b>{currentFolder.name}</b>
              </span>
            </div>
          )}
        </section>
      </div>

      <Toast
        message={explorer.toast}
        onClose={explorer.clearToast}
        duration={3500}
        className={isMobile ? 'file-explorer-toast-mobile' : ''}
      />
    </div>
  );
}
